import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { saveSessionEvent } from "../../../session";
import * as Steps from '../AmericoLoanProtection/steps/index';
import {
  setBasicInfo,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_COVR_FULLUW_KEY,
  SESSION_STORAGE_ACTIVE_PURL,
  SESSION_STORAGE_UTM_DATA,
  SESSION_STORAGE_ANALYSIS_KEY,
  getBasicInfo,
  SESSION_STORAGE_AMERICO_OBJECT_KEY,
  storeConsumerApplicationId
} from '../../../sessionStorage';
import '../AmericoLoanProtection/LoanProtection.scss';
import { HeaderProgressTracker } from "../Common/HeaderStepper/HeaderProgressTracker";
import Age from "../../Helpers/Dates/Age";
import { getTermLengthFromAge } from "../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths";
import { getFaceAmountFromAge } from "../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts";
import { calculateBMI, calculateTotalHeight } from '../../Helpers/Conversion';
import { CheckIneligibility } from "../AmericoLoanProtection/Helpers/CheckIneligibility";

const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL

export default function PartnerLoanProtection(props) {
  
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent(); }, [location]);

  const { path } = useParams();

  const SESSION_STORAGE_KEY = "AMERICO_PARTNER_QUOTE";

  const sessionStorageFormState = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY));

  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');

  const basicInfo = getBasicInfo();
  const mctQuoterData = getSessionStorageItem(SESSION_STORAGE_AMERICO_OBJECT_KEY);
  const age = basicInfo && Age(basicInfo.birthdate);
  const getActivePurlData = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL);
  const isActivePurl = getActivePurlData ? getActivePurlData.active : false;
  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const account = accountObj.account;
  const accountId = account.accountId;
  const analysisInfo = getSessionStorageItem(SESSION_STORAGE_ANALYSIS_KEY);
  const promotedProducts = JSON.parse(account.promotedProducts);
  const ineligibleProducts = (promotedProducts && promotedProducts.filter(p => p.isEnabled && p.productHost.toLowerCase() === "americo")) || [];
  
  if (props.landing && sessionStorageFormState) {
    sessionStorageFormState.currentStep = 1;
  };

  useEffect(() => {
    if (basicInfo.height && basicInfo.weight) {
      const weight = basicInfo.weight.split(" ")[0];
      const totalHeightInches = calculateTotalHeight(basicInfo);
      const calculatedBMI = calculateBMI(totalHeightInches, weight);
      basicInfo.bmi = calculatedBMI;
      setBasicInfo(basicInfo);
    }
  }, [basicInfo.height, basicInfo.weight]);

  const loanProtProductSettings = accountObj.accountProducts.find(accPrd => accPrd.productLink === '/landing/loan-protection');
  const newDefaultFormState = {
    currentStep: 1,
    policyNumber: "",
    relatedFinancialInstitution: account.accountName,
    submittingAgentCode: loanProtProductSettings && !(loanProtProductSettings.submittingAgentCode === null || loanProtProductSettings.submittingAgentCode === '') ? loanProtProductSettings.submittingAgentCode : 'CVR99B',
    steps: {
      basicInfo: Steps.BasicInfo.DefaultFormState(prefill, basicInfo, false, mctQuoterData, isActivePurl),
      quoter: Steps.Quoter.DefaultFormState(prefill, basicInfo, mctQuoterData),
      personalInfo: Steps.PersonalInfo.DefaultFormState(prefill, basicInfo),
      gettingToKnowYou: Steps.GettingToKnowYou.DefaultFormState(prefill, basicInfo, analysisInfo),
      identityVerification: Steps.IdentityVerification.DefaultFormState(prefill, basicInfo, analysisInfo),
      beneficiary: Steps.Beneficiary.DefaultFormState(prefill),
      personalHistory: Steps.PersonalHistory.DefaultFormState(prefill),
      medicalHistory: Steps.MedicalHistory.DefaultFormState(prefill, mctQuoterData),
      agreementToUnderwriting: Steps.AgreementToUnderwriting.DefaultFormState(prefill),
      policyPurposeConfirmation: Steps.PolicyPurposeConfirmation.DefaultFormState(prefill),
      financialInformation: Steps.FinancialInformation.DefaultFormState(prefill),
      appointment: Steps.Appointment.DefaultFormState(prefill, basicInfo),
    },
    pages: {
      preQuote: {
        coverageAmountQuote: (basicInfo && basicInfo.faceAmount || getFaceAmountFromAge(age)),
        healthClass: (basicInfo && basicInfo.healthClass || "PP"),
        termQuote: (basicInfo && basicInfo.coverageLength || getTermLengthFromAge(age)),
      },
      quoter: Steps.Quoter.DefaultFormState(prefill, basicInfo),
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
    adminMode: false,
    adminCode: "",
    decisionResponse: null,
    fieldsChangedAfterUnderwriting: [],
    oneClickAccountId: account.oneClickAccountId,
    consumerAccountId: accountId,
    isAmerico3: true,
    sessionId: accountObj.sessionId 
  };

  const initialFormState = sessionStorageFormState || newDefaultFormState;
  const mctFlowPages = getSessionStorageItem(SESSION_STORAGE_COVR_FULLUW_KEY);
  const isPartnerLoanProtection = true;

  const [formState, setFormState] = useState(initialFormState);

  // update the formState when the url path changes the step
  useEffect(
    () => {
      if (!path) {
        return;
      }

      const nextStep = steps.find(s => s.path === path);

      // prevent submitted app alterations
      if ((!sessionStorageFormState && formState.currentStep === (steps.length - 2)) || !sessionStorageFormState) {

        // if they are backing or directly going to another step, send them to product root
        if (formState.currentStep !== nextStep.step) {
          navigate('/partner-loan-protection/getting-started');
        }
      }

      if (formState.currentStep === 6 && formState.currentStep > nextStep.step) {
        navigate('/partner-loan-protection/underwriting-offer');
      }

      if (!nextStep || formState.currentStep === nextStep.step) {
        return;
      }
      const newFormState = { ...formState, currentStep: nextStep.step };
      setFormState(newFormState);
    }, [path]);

  const postConsumerApplication = (newFormState) => {
    if (!newFormState) { newFormState = formState; }
    const newForm = mctQuoterData ? { ...newFormState, logoUrl: mctQuoterData.logoUrl, mctQuoterData: mctQuoterData, mctFlowData: mctFlowPages } : newFormState;
    return fetch(`${covrServicesUrl}/api/ConsumerApplication/Post?accountId=${accountId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        'SessionId': accountObj.sessionId,
        'X-Consumer-Application-ID': accountObj.consumerApplicationId,
      },
      body: JSON.stringify(newForm),
    }).then(storeConsumerApplicationId);

  };

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    // save email from personalInfo to basicInfo
    if (formState.currentStep === 2) {
      newFormState.steps.basicInfo.email = newFormState.steps.personalInfo.emailAddress;
    }

    if (CheckIneligibility(formState, isPartnerLoanProtection)) {
      goToStep('ineligible');
      return;
    }

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));
    const newForm = mctQuoterData ? { ...newFormState, logoUrl: mctQuoterData.logoUrl, mctQuoterData: mctQuoterData, mctFlowData: mctFlowPages } : newFormState;
    newFormState.UTMs = getSessionStorageItem(SESSION_STORAGE_UTM_DATA) || "";

    fetch(`${covrServicesUrl}/api/ConsumerApplication/Post?accountId=${accountId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        'SessionId': accountObj.sessionId,
        'X-Consumer-Application-ID': accountObj.consumerApplicationId,
      },
      body: JSON.stringify(newForm),
    }).then(storeConsumerApplicationId);

    if (nextStep.step === 4) {
      saveSessionEvent(true);
    };

    navigate(`/partner-loan-protection/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const setPolicyNumber = (value) => {
    setFormState({ ...formState, policyNumber: value });
  };

  const handlePaymentComplete = (appId, policyEffectiveDate) => {
    setFormState({ ...formState, appIdentifier: appId, policyEffectiveDate: policyEffectiveDate });
  };

  const handleApprovedPremiumUpdate = (oldPremium, newPremium) => {
    setFormState({ ...formState, originalPremium: oldPremium, steps: { ...formState.steps, quoter: { ...formState.steps['quoter'], premium: newPremium } } });
  };

  const onBack = () => {
    if (formState.currentStep <= 1) {
      navigate('/', { replace: true });
    }

    const backStep = steps.find(s => s.step === formState.currentStep - 1);
    if (!backStep) {
      throw new Error("onBack did not find a previous step");
    }

    const newFormState = { ...formState, currentStep: backStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));

    navigate(`/partner-loan-protection/${backStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    if (step === 'basicInfo' && field === 'tobacco') {
      const form = formState;
      form.steps.medicalHistory.nicotineUse = value;
      setFormState(form);
    }
    setFormState({ ...formState, steps: { ...formState.steps, [step]: { ...formState.steps[step], [field]: value } } });
  };

  const goToStep = (path) => {
    const nextStep = steps.find(s => s.path === path);
    if (!nextStep) {
      throw new Error("goToStep did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newFormState));

    navigate(`/partner-loan-protection/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const getHeaderText = () => {
    const currentStep = formState.currentStep;
    if (currentStep === 1) {
      return "Partner LoanMatch Protector";
    }
    return "LoanMatch Protector";
  };

  // hook onNextApply
  const steps = [
    {
      step: 1,
      path: 'getting-started',
      progressTitle: 'Getting Started',
      component: (
        <Steps.GettingStarted
          onFieldChange={(field, value, page) => onFieldChange(page, field, value)}
          onBack={onBack}
          onNext={onNext}
          basicInfo={formState.steps.basicInfo}
        />
      ),
      hideStepsDropdown: true,
      categoryName: 'Getting Started',
    },
    {
      step: 2,
      path: 'insured-info',
      component: (
        <Steps.InsuredInfo
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          onBack={onBack}
          onNext={onNext}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          fullFormState={formState}
          postConsumerApplication={postConsumerApplication}
          isPartnerLoanProtection={isPartnerLoanProtection}
        />
      ),
      categoryNumber: 1,
      categoryName: 'Proposed Insured Information',
    },
    {
      step: 3,
      path: 'personal-history',
      component: (
        <Steps.PersonalMedicalHistory
          fullFormState={formState}
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          onBack={onBack}
          onNext={onNext}
          basicInfo={formState.steps.basicInfo}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          isPartnerLoanProtection={isPartnerLoanProtection}
        />
      ),
      categoryNumber: 2,
      categoryName: 'Personal and Medical History',
    },
    {
      step: 4,
      path: 'beneficiary',
      component: (
        <Steps.Beneficiary
          formState={formState.steps.beneficiary}
          onFieldChange={(field, value) => onFieldChange('beneficiary', field, value)}
          onBack={onBack}
          onNext={onNext}
          maritalStatus={formState.steps.gettingToKnowYou.maritalStatus}
        />
      ),
      categoryNumber: 3,
      categoryName: 'Beneficiary',
    },
    {
      step: 5,
      path: 'confirmation',
      component: (
        <Steps.Confirmation
          fullFormState={formState}
          onFieldChange={(page, field, value) => onFieldChange(page, field, value)}
          basicInfo={formState.steps.basicInfo}
          onBack={onBack}
          onNext={onNext}
          setPolicyNumber={setPolicyNumber}
          onDeclined={() => goToStep('ineligible')}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
        />
      ),
      categoryNumber: 4,
      categoryName: 'Disclosures and Agreements',
    },
    {
      step: 6,
      path: 'underwriting-offer',
      component: (
        <Steps.UnderWritingOffer
          formState={formState}
          underWritingOnFieldChange={onFieldChange}
          offerOnFieldChange={(field, value) => onFieldChange('quoter', field, value)}
          onBack={onBack}
          onNext={onNext}
          basicInfo={formState.steps.basicInfo}
          onDeclined={() => goToStep('ineligible')}
          handleApprovedPremiumUpdate={(oldItem, newItem) => handleApprovedPremiumUpdate(oldItem, newItem)}
          quoter={formState.steps.quoter}
          onMultiFieldChange={(fs) => {
            setFormState(fs);
          }}
          onDecision={(updatedFormState) => {
            setFormState({ ...updatedFormState });
          }}
          onCoverageChanged={() => {
            const changed = formState.fieldsChangedAfterUnderwriting;
            changed.push('coverage');
            setFormState({ ...formState, fieldsChangedAfterUnderwriting: changed });
          }}
        />
      ),
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 7,
      path: 'payment-information',
      component: <Steps.FinancialInformation
        formState={formState.steps.financialInformation}
        fullFormState={formState}
        onFieldChange={(field, value) => onFieldChange('financialInformation', field, value)}
        onNext={onNext}
        onBack={onBack}
        handlePaymentComplete={handlePaymentComplete}
      />,
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 8,
      path: 'sign',
      component: <Steps.Sign
        formState={formState}
        SSNNumber={true}
        onNext={onNext}
        onBack={onBack}
        isAmerico3={true}
        redirectPath={"/partner-loan-protection/EsignReturn"}
      />,
      categoryNumber: 5,
      categoryName: 'Offer and Payment',
    },
    {
      step: 9,
      path: 'status',
      component: <Steps.Status
        formState={formState}
        productType={"Americo"}
        basicInfo={formState.steps.basicInfo} />,
      categoryNumber: 5,
      categoryName: 'Summary',
      lastStep: true,
    },
    {
      step: 10,
      path: 'thank-you',
      progressTitle: 'ThankYou',
      component: <Steps.ThankYou
        sessionStorageKey={SESSION_STORAGE_KEY}
        name={formState.steps.appointment.appointment}
        date={formState.steps.appointment.specificApptTime}
        basicInfo={formState.steps.basicInfo} />,
      hideProgressTracker: true,
      hideMainHeader: false,
    },
    {
      step: 11,
      path: 'ineligible',
      component: <Steps.Ineligible
        ineligibleProducts={ineligibleProducts} />,
      categoryNumber: 5,
      categoryName: 'Ineligibility',
    },
  ];

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };
  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];

  const categories = steps.filter((o) => typeof o.categoryNumber !== 'undefined');
  const totalCategories = Math.max(...categories.map((o) => { return o.categoryNumber; }));
  const totalCategoriesStep = Math.max(...categories.map((o) => { return o.categoryStep; }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);
  const removePadding = [
    "/partner-loan-protection/ineligible",
    "/partner-loan-protection/underwriting-offer"
  ].includes(location.pathname);

  return (
    <div>
      <Helmet>
        <title>{`Partner Loan Protector - ${currentStepObj?.categoryName || currentStepObj?.progressTitle}`}</title>
      </Helmet>
      <HeaderProgressTracker
        breadCrumb={currentStepObj.categoryName ? currentStepObj.categoryName.toUpperCase() : ''}
        breadCrumbLinks={{ name: 'PARTNER LOANMATCH PROTECTOR', href: '/partner-loan-protection' }}
        isBreadCrumb={true}
        title={getHeaderText()}
        showContainer={currentStepObj.showContainer}
        hidden={props.landing || currentStepObj.hideMainHeader}
        modernizeContainer={true}
        hideProgressTracker={currentStepObj.hideProgressTracker}
        headerProductsText={true}
        stepName={currentStepObj.progressTitle || currentStepObj.categoryName}
        step={currentStepObj.categoryNumber}
        totalSteps={totalCategories}
        whiteBg={currentStepObj.whiteBackground}
        percent={percent}
        hideStepText={currentStepObj.categoryNumber === undefined}
        fullWidth={currentStepObj.fullWidth}>
        <div className={`americoLoanProtector ${removePadding ? "" : "pt-5"} ${currentStepObj.step > 2 && currentStepObj.step !== 9 ? 'border-class' : ''}`}>
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}

PartnerLoanProtection.propTypes = {
  landing: PropTypes.bool,
};
